/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import axiosInstance from './axios';
import Product, { ClothingSizeTypeStock } from 'types/models/Product';
import ProductImage from 'types/models/ProductImage';
import ProductCategory from 'types/models/ProductCategory';
import PaginationData from 'types/models/PaginationData';
import { parseCommerce } from './commercesService';
import { parseTomorrowPlans } from './venuesServices';
import TomorrowGlobalProduct from 'types/models/TomorrowGlobalProduct';

export const getProductsByCommerce = async (commerceId: string): Promise<Product[] | []> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `/commerces/${commerceId}/products/all`,
    });
    return parseProducts(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getPromotionsByVenue = async (venueId: string, page?: number): Promise<PaginationData<Product> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `/venues/${venueId}/promotions?page=${page ?? 1}&perPage=${3}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseProducts(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
      lastPage: paginationData.last_page,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getPromotionsByCommerce = async (venueId: string, commerceId: string): Promise<Product[] | []> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `/venues/${venueId}/promotions?commerceId=${commerceId}`,
    });
    return parseProducts(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getGuanicionesExtrasProduct = async (productId: string): Promise<Product[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `/products/${productId}/guarniciones-extras`,
    });
    return parseProducts(data);
  } catch (error) {
    throw error;
  }
};

export const parseProducts = (products: any[]): Product[] => {
  return products.map((product: any): Product => parseProduct(product));
};

export const parseProduct = (product: any): Product => {
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    price: product.price,
    images: parseImages(product.images),
    isClothing: product.is_clothing === 1,
    clothingSizeStocks: product.clothing_sizes_stocks ? parseClothingSizeTypeStocks(product.clothing_sizes_stocks) : [],
    stock: product.stock,
    isSuggested: product.is_suggested,
    showInList: product.show_in_list,
    minimumAmount: product.minimum_amount,
    typeId: product.type_id,
    estimatedPreparationTime: product.estimated_preparation_time ?? undefined,
    promotionBannerBackgroundColor: product.promotion_banner_background_color ?? undefined,
    promotionBannerColorTitle: product.promotion_banner_color_title ?? undefined,
    isUsedForTomorrowBenefit: product.is_used_for_tomorrow_benefit,
    category: parseCategory(product.category),
    commerce: product.commerce ? parseCommerce(product.commerce) : undefined,
    updatedAt: product.updated_at,
    withGuarnicionProductId: product.pivot?.with_guarnicion_product_id ?? undefined,
    withExtraProductId: product.pivot?.with_extra_product_id ?? undefined,
    globalTomorrowBenefitProductId: product.tomorrow_global_benefit_product_id,
    tomorrowGlobalProduct: product.tomorrow_global_product
      ? parseTomorrowGlobalProduct(product.tomorrow_global_product)
      : undefined,
  };
};

export const parseImages = (data: any[]): ProductImage[] => {
  return data.map((item) => ({
    productId: item.pivot.product_id,
    productImageTypeId: item.pivot.product_image_type_id,
    path: item.path,
  }));
};

export const parseProductsForStockCheck = (products: any[]): Product[] => {
  return products.map((product: any): Product => parseProductForStockCheck(product));
};

const parseProductForStockCheck = (product: any): Product => {
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    price: product.price,
    isClothing: product.is_clothing === 1,
    clothingSizeStocks: product.clothing_sizes_stocks ? parseClothingSizeTypeStocks(product.clothing_sizes_stocks) : [],
    images: [],
    stock: product.stock,
    isSuggested: product.is_suggested,
    showInList: product.show_in_list,
    minimumAmount: product.minimum_amount,
    typeId: product.type_id,
    estimatedPreparationTime: product.estimated_preparation_time ?? undefined,
    updatedAt: product.updated_at,
    clothingSizeType: Boolean(product.is_clothing)
      ? { id: parseInt(product.clothing_size.id), description: product.clothing_size.description }
      : undefined,
    isUsedForTomorrowBenefit: product.is_used_for_tomorrow_benefit,
    globalTomorrowBenefitProductId: product.tomorrow_global_benefit_product_id,
    tomorrowGlobalProduct: product.tomorrow_global_product
      ? parseTomorrowGlobalProduct(product.tomorrow_global_product)
      : undefined,
  };
};

const parseCategory = (category: any): ProductCategory => {
  return {
    id: category.id,
    description: category.description,
    requiresIdentification: Boolean(category.requires_identification),
    image: category.path,
    order: category.order,
  };
};

export const parseOrderDetailProduct = (product: any): Product => ({
  id: product.id,
  name: product.name,
  description: product.description,
  price: product.price,
  isClothing: product.is_clothing === 1,
  clothingSizeStocks: product.clothing_sizes_stocks ? parseClothingSizeTypeStocks(product.clothing_sizes_stocks) : [],
  isSuggested: product.is_suggested,
  showInList: product.show_in_list,
  minimumAmount: product.minimum_amount,
  typeId: product.type_id,
  stock: product.stock,
  images: parseImages(product.images),
  estimatedPreparationTime: product.estimated_preparation_time ?? undefined,
  isUsedForTomorrowBenefit: product.is_used_for_tomorrow_benefit,
  updatedAt: product.updated_at,
  globalTomorrowBenefitProductId: product.tomorrow_global_benefit_product_id,
  tomorrowGlobalProduct: product.tomorrow_global_product
    ? parseTomorrowGlobalProduct(product.tomorrow_global_product)
    : undefined,
});

const parseClothingSizeTypeStocks = (clothingSizeStocks: any[]): ClothingSizeTypeStock[] => {
  return clothingSizeStocks
    .filter((clothingSizeStock) => clothingSizeStock.stock !== 0)
    .map((clothingSizeStock) => ({
      id: clothingSizeStock.clothing_size_type_id,
      description: clothingSizeStock.clothing_size_type.description,
      stock: clothingSizeStock.stock,
    }));
};

const parseTomorrowGlobalProduct = (tomorrowGlobalProduct: any): TomorrowGlobalProduct => {
  return {
    id: tomorrowGlobalProduct.id,
    amountInStockAvailableForTomorrowBenefit:
      tomorrowGlobalProduct.amount_in_stock_available_for_tomorrow_benefit ?? undefined,
    tomorrowPlans: tomorrowGlobalProduct.tomorrow_plans ? parseTomorrowPlans(tomorrowGlobalProduct.tomorrow_plans) : [],
    tomorrowBenefitDiscountPercentage: tomorrowGlobalProduct.tomorrow_benefit_discount_percentage ?? undefined,
  };
};
